import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, request } from "../api/client";
import { classroomCommandBody } from "./state";
import type { ClassroomCommand, ClassroomCommandInput, ClassroomPage, ClassroomSection } from "./types";

export function classroomRead<T>(path: string, query: Record<string, string> = {}, signal?: AbortSignal) {
  const values = new URLSearchParams(Object.entries(query).filter(([, v]) => !!v));
  return request<T>("/classrooms/" + path + (values.size ? "?" + values : ""), "GET", undefined, signal);
}
export function useClassroomCommand(section: ClassroomSection | null, saved: (value: ClassroomSection) => void): ClassroomCommand {
  const [busy, setBusy] = useState(false), [uncertain, setUncertain] = useState(false), [error, setError] = useState<unknown>();
  const running = useRef(false), attempt = useRef<object | null>(null);
  const send = async (input?: ClassroomCommandInput) => {
    if (running.current) return false;
    if (!attempt.current && input) attempt.current = classroomCommandBody(input, section, crypto.randomUUID());
    if (!attempt.current) return false;
    running.current = true; setBusy(true); setError(undefined);
    try {
      const value = await request<ClassroomSection>("/classrooms/command", "POST", attempt.current);
      const scope = attempt.current as { action: string; sectionId?: string; organizationId?: string };
      if (!value?.sectionId || !Number.isSafeInteger(value.version) || value.version < 1 ||
        scope.action !== "create" && value.sectionId !== scope.sectionId ||
        value.organizationId !== (scope.action === "create" ? scope.organizationId : section?.organizationId)) throw new Error("Invalid command response");
      attempt.current = null; setUncertain(false); saved(value); return true;
    } catch (failure) {
      const unknown = !(failure instanceof ApiError) || failure.status >= 500 || failure.status === 408;
      if (!unknown) attempt.current = null;
      setUncertain(unknown); setError(failure); return false;
    } finally { running.current = false; setBusy(false); }
  };
  useEffect(() => {
    if (!busy && !uncertain) return;
    const leave = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", leave);
    return () => window.removeEventListener("beforeunload", leave);
  }, [busy, uncertain]);
  return { send, busy, uncertain, error, locked: busy || uncertain };
}
export function useClassroomPage<T>(path: string, sectionId: string, revision: number, enabled = true, extraQuery: Record<string, string> = {}) {
  const [items, setItems] = useState<T[]>([]), [nextCursor, setNextCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState<unknown>();
  const controller = useRef<AbortController | null>(null);
  const extra = JSON.stringify(extraQuery);
  const load = useCallback(async (cursor = "") => {
    if (!enabled) return;
    controller.current?.abort(); const current = new AbortController(); controller.current = current;
    setBusy(true); setError(undefined); if (!cursor) { setItems([]); setNextCursor(null); }
    try {
      const result = await classroomRead<ClassroomPage<T>>(path, { ...JSON.parse(extra), sectionId, cursor }, current.signal);
      if (!Array.isArray(result.items)) throw new Error("Invalid list response");
      if (!current.signal.aborted) { setItems(previous => cursor ? [...previous, ...result.items] : result.items); setNextCursor(result.nextCursor || null); }
    } catch (failure) { if (!current.signal.aborted) { setError(failure); setItems([]); setNextCursor(null); } }
    finally { if (!current.signal.aborted) setBusy(false); }
  }, [path, sectionId, revision, enabled, extra]);
  useEffect(() => { void load(); return () => controller.current?.abort(); }, [load]);
  return { items, nextCursor, busy, error, load };
}
